import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { prepareHeaders } from './utils';
import { LogDeviceWhitelist } from '../features/app/context/log-device-whitelist/components/LogDeviceWhitelistForm';
import { config } from '../config';
import { DEVICE_TYPE } from '../features/app/context/device-sidebar/device-state.types';

const REQUEST_MAPPING = '/log-device-whitelist';
const TAG = 'LogDeviceWhitelist';

const transformInput = (logDeviceWhitelist: LogDeviceWhitelist) => ({
    ...logDeviceWhitelist,
    type: logDeviceWhitelist.type.toLowerCase(),
});

const transformInputTbm = (logDeviceWhitelist: LogDeviceWhitelist) => ({
    ...logDeviceWhitelist,
    type: logDeviceWhitelist.type.replace(/\d+$/, '').toLowerCase(),
    originator: logDeviceWhitelist.originator?.replace(/(\D+)\d+:/, '$1:')
});

export const logDeviceWhitelistCm4Api = createApi({
    reducerPath: 'logDeviceWhitelistCm4',
    tagTypes: [TAG],
    baseQuery: fetchBaseQuery({
        baseUrl: config.backend.DEVICE_STATE_SERVICE,
        prepareHeaders,
    }),
    endpoints: (builder) => ({
        getAllCm4: builder.query<LogDeviceWhitelist[], void>({
            query: () => REQUEST_MAPPING,
            providesTags: [TAG],
            transformResponse: (response: LogDeviceWhitelist[]) =>
                response
                    .map(item => ({
                        ...item,
                        type: item.type.toUpperCase() as DEVICE_TYPE,
                    }))
                    .filter(item => item.type === DEVICE_TYPE.CM4),
        }),
        getAllTbm3: builder.query<LogDeviceWhitelist[], void>({
            query: () => REQUEST_MAPPING,
            providesTags: [TAG],
            transformResponse: (response: LogDeviceWhitelist[]) =>
                response
                    .map(item => ({
                        ...item,
                        type: item.type.toUpperCase() as DEVICE_TYPE,
                    }))
                    .filter(item => item.type.toLocaleLowerCase() === 'tbm'),
        }),
        getDevice: builder.query<LogDeviceWhitelist, string>({
            query: (originator) => `${REQUEST_MAPPING}/${originator}`,
        }),
        addDevice: builder.mutation<LogDeviceWhitelist[], LogDeviceWhitelist>({
            query: (newLogDeviceWhitelist) => {
              const body =
                newLogDeviceWhitelist.type === DEVICE_TYPE.CM4
                  ? [transformInput(newLogDeviceWhitelist)]
                  : [transformInputTbm(newLogDeviceWhitelist)];
              return {
                url: REQUEST_MAPPING,
                method: 'POST',
                body,
              };
            },
            invalidatesTags: [TAG],
          }),
        updateDevice: builder.mutation<LogDeviceWhitelist[], LogDeviceWhitelist>({
            query: (updatedLogDeviceWhitelist) => ({
                url: REQUEST_MAPPING,
                method: 'PUT',
                body: [transformInput(updatedLogDeviceWhitelist)],
            }),
            invalidatesTags: [TAG],
        }),
        deleteDevice: builder.mutation<void, string>({
            query: (originator) => ({
                url: `${REQUEST_MAPPING}/${originator}`,
                method: 'DELETE',
            }),
            invalidatesTags: [TAG],
        }),
    }),
});

export const {
    useGetAllCm4Query,
    useGetAllTbm3Query,
    useGetDeviceQuery,
    useAddDeviceMutation,
    useUpdateDeviceMutation,
    useDeleteDeviceMutation,
} = logDeviceWhitelistCm4Api;